<script lang="ts">
  import { createEventDispatcher, onMount, tick } from "svelte";
  import { tweened } from "svelte/motion";
  import { cubicOut } from "svelte/easing";

  interface TabItem {
    value: string;
    label: string;
  }

  // A tab switcher with fewer than two tabs has nothing to switch between,
  // so we encode the "two or more" invariant in the type system rather than
  // rely on runtime checks. Inline array literals narrow to tuples
  // automatically, so all existing call sites type-check unchanged.
  export type TabItems = readonly [TabItem, TabItem, ...TabItem[]];

  interface Props {
    value?: string;
    tabs: TabItems;
    fullWidth?: boolean;
  }

  let { value: controlledValue = $bindable(""), tabs, fullWidth = false }: Props = $props();

  // svelte-ignore state_referenced_locally
  if (!controlledValue) {
    controlledValue = tabs[0].value;
  }

  // Supports callers that use one-way `value` + `on:change` instead of bind:value
  // @deprecated(createEventDispatcher)
  const dispatch: (type: "change", detail: string) => void = createEventDispatcher();

  function select(next: string) {
    if (next !== controlledValue) {
      controlledValue = next;
      dispatch("change", next);
    }
  }

  let toggleContainer = $state<HTMLElement>();
  let buttonElements: Record<string, HTMLElement> = $state({});
  let indicatorReady = false;

  // @deprecated(tweened)
  const indicatorX = tweened(0, { duration: 0, easing: cubicOut });
  const indicatorWidth = tweened(0, { duration: 0, easing: cubicOut });

  function updateIndicator(animate = indicatorReady) {
    if (!toggleContainer || !buttonElements[controlledValue]) return;

    const activeButton = buttonElements[controlledValue];
    const rect = activeButton.getBoundingClientRect();
    const containerRect = toggleContainer.getBoundingClientRect();

    const options = animate ? { duration: 300 } : { duration: 0 };
    indicatorX.set(rect.left - containerRect.left, options);
    indicatorWidth.set(rect.width, options);

    if (!indicatorReady) {
      indicatorReady = true;
    }
  }

  $effect(() => {
    if (controlledValue && Object.keys(buttonElements).length > 0) {
      updateIndicator();
    }
  });

  function registerButton(node: HTMLElement, tabValue: string) {
    buttonElements[tabValue] = node;
    return {
      destroy() {
        delete buttonElements[tabValue];
      },
    };
  }

  onMount(() => {
    if (!toggleContainer) return;
    tick().then(() => updateIndicator(false));

    const resizeObserver = new ResizeObserver(() => updateIndicator());
    resizeObserver.observe(toggleContainer);

    return () => resizeObserver.disconnect();
  });
</script>

<div
  bind:this={toggleContainer}
  class="relative flex items-center rounded-lg bg-mono-000 py-1 pr-px shadow-border-inset {fullWidth
    ? 'w-full'
    : 'w-fit'}"
  role="tablist"
  aria-label="Tab group"
>
  <div
    class="absolute z-0 mx-px h-5/6 rounded-md bg-mono-300 pr-1"
    style="transform: translateX({$indicatorX}px); width: {$indicatorWidth}px;"
  ></div>
  {#each tabs as tab (tab.value)}
    <button
      type="button"
      role="tab"
      aria-selected={controlledValue === tab.value}
      class="toggle-item relative z-10 mx-0.5 rounded-lg px-3 text-sm transition-colors duration-200 ease-in-out {fullWidth
        ? 'flex-1'
        : ''}"
      use:registerButton={tab.value}
      data-value={tab.value}
      class:text-foreground-primary={controlledValue === tab.value}
      class:text-foreground-secondary={controlledValue !== tab.value}
      onclick={() => select(tab.value)}
    >
      {tab.label}
    </button>
  {/each}
</div>
